<template>
  <div v-if="customer">
    <v-layout mt-4 child-flex>
      <div>
        <v-tabs @change="onTabChange">
          <v-tab> <v-icon left>description</v-icon>General </v-tab>
          <v-tab> <v-icon left>shopping_cart</v-icon>Orders </v-tab>
          <v-tab> <v-icon left>room</v-icon>Billing Addresses </v-tab>
          <v-tab> <v-icon left>room</v-icon>Shipping Addresses </v-tab>
          <v-tab> <v-icon left>people</v-icon>Users </v-tab>
          <v-tab> <v-icon left>attach_money</v-icon>Pricing </v-tab>
          <v-tab-item>
            <div>
              <v-layout mt-4 pt-4>
                <v-flex xs12 sm4>
                  <v-flex xs12 pl-2 pr-2>
                    <v-text-field
                      v-model="customer.name"
                      outlined
                      label="Name"
                      dense
                    />
                  </v-flex>
                  <v-flex xs12 pl-2 pr-2>
                    <v-text-field
                      v-model="customer.email"
                      outlined
                      dense
                      label="Email"
                    />
                  </v-flex>

                  <v-flex xs12 pl-2 pr-2>
                    <v-combobox
                      v-model="customer.assignedReps"
                      :items="associates"
                      label="Assigned Reps"
                      :item-text="associateName"
                      item-value="_id"
                      multiple
                      outlined
                      dense
                      chips
                    >
                      <template v-slot:selection="data">
                        <v-chip
                          small
                          :key="JSON.stringify(data.item._id)"
                          v-bind="data.attrs"
                        >
                          <v-avatar
                            class="accent white--text"
                            left
                            v-text="
                              data.item.lastName.slice(0, 1).toUpperCase()
                            "
                          ></v-avatar>
                          {{ data.item.lastName }}, {{ data.item.firstName }}
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-flex>
                  <v-flex xs12 pl-2 pr-2>
                    <v-textarea
                      v-model="customer.notes"
                      outlined
                      dense
                      textarea
                      rows="4"
                      label="Notes"
                    />
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-layout v-if="canUpdateCustomer">
                <v-flex mt-4 ml-4 mb-4>
                  <v-btn @click="saveCompany" color="primary">Save</v-btn>
                </v-flex>
              </v-layout>
              <v-divider />
            </div>
          </v-tab-item>
          <v-tab-item>
            <v-layout child-flex mt-4 pa-4>
              <Table
                :headers="ordersTableHeaders"
                :items="orders"
                icon="domain"
                :actions="ordersTableActions"
                :items-per-page="20"
                :primaryActionText="canCreateOrder ? 'New Order' : false"
                primaryActionIcon="add"
                @primary-action-event="gotoNewOrder"
              />
            </v-layout>
          </v-tab-item>
          <v-tab-item>
            <v-layout child-flex mt-4 pa-4>
              <Table
                :headers="addressHeaders"
                :items="customer.billingAddresses"
                icon="room"
                client-search
                hide-footer
                :show-banner="false"
                :actions="addressActions"
                :primaryActionText="canUpdateCustomer ? 'Add Address' : false"
                primaryActionIcon="add"
                @primary-action-event="setAddAddress('billingAddresses')"
              />
            </v-layout>
          </v-tab-item>
          <v-tab-item>
            <v-layout child-flex mt-4 pa-4>
              <Table
                :headers="addressHeaders"
                :items="customer.shippingAddresses"
                icon="room"
                :client-search="true"
                hide-footer
                :show-banner="false"
                :actions="addressActions"
                :items-per-page="20"
                :primaryActionText="canUpdateCustomer ? 'Add Address' : false"
                primaryActionIcon="add"
                @primary-action-event="setAddAddress('shippingAddresses')"
              />
            </v-layout>
          </v-tab-item>
          <v-tab-item>
            <v-layout child-flex mt-4 pa-4>
              <Table
                :headers="userHeaders"
                :items="customer.users"
                icon="person"
                :client-search="true"
                hide-footer
                :show-banner="false"
                :actions="userActions"
                :items-per-page="20"
              />
            </v-layout>
          </v-tab-item>
          <v-tab-item v-if="canReadCustomerPricing">
            <v-tabs class="mt-6" child-flex>
              <v-tab left>Category / Subcategory markups</v-tab>
              <v-tab left>Individual Product Pricing</v-tab>
              <v-tab-item>
                <v-flex xs12 sm4 mt-6>
                  <div v-for="(category, index) in markups" :key="category._id">
                    <v-layout>
                      <v-flex xs12 sm8 md4>
                        <v-subheader>{{ category.category.name }}</v-subheader>
                      </v-flex>
                      <v-flex xs12 sm8 md4>
                        <v-text-field
                          :readonly="!canUpdateCustomerPricing"
                          outlined
                          dense
                          value="1.5"
                          min="1.01"
                          v-model="markups[index].value"
                          type="number"
                          @input="updateAndSavePricing"
                        />
                      </v-flex>
                    </v-layout>
                    <div
                      v-if="
                        category.subcategories && category.subcategories.length
                      "
                    >
                      <h5 class="offset-sm-1 mb-2">Subcategories:</h5>
                      <v-layout
                        v-for="(subcategory, idx) in category.subcategories"
                        :key="idx"
                        class="offset-sm-1"
                      >
                        <v-flex xs12 sm8 md4>
                          <v-subheader>
                            {{ subcategory.subcategory.name }}
                          </v-subheader>
                        </v-flex>
                        <v-text-field
                          type="number"
                          :readonly="!canUpdateCustomerPricing"
                          dense
                          outlined
                          value="1.5"
                          min="1.01"
                          v-model="markups[index].subcategories[idx].value"
                          @input="updateAndSavePricing"
                        />
                      </v-layout>
                    </div>
                  </div>
                </v-flex>
              </v-tab-item>
              <v-tab-item>
                <v-layout wrap>
                  <v-flex xs12 md4>
                    <v-card class="ml-2 mr-4 mt-12">
                      <v-card-title>Individual Product Pricing</v-card-title>
                      <v-list three-line>
                        <div
                          v-for="(
                            item, index
                          ) in customer.individualProductPrices"
                          :key="index"
                        >
                          <v-list-item three-line>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ item.productCode }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                {{ item.manufacturer }}
                              </v-list-item-subtitle>

                              <v-list-item-subtitle>
                                {{ item.description[0].value }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle>
                                <v-radio-group
                                  v-model="item.priceSelection"
                                  mb-0
                                  pb-0
                                >
                                  <v-layout align="center">
                                    <v-radio
                                      @change="updateAndSavePricing"
                                      label="Markup"
                                      value="customMarkup"
                                    ></v-radio>
                                    <v-text-field
                                      @input="updateAndSavePricing"
                                      v-model="item.markup"
                                      :readonly="
                                        item.priceSelection === 'customPrice' ||
                                        !canUpdateCustomerPricing
                                      "
                                      :disabled="
                                        item.priceSelection === 'customPrice' ||
                                        !canUpdateCustomerPricing
                                      "
                                      type="number"
                                      min="1.01"
                                      class="ml-6 mb-4"
                                    ></v-text-field>
                                  </v-layout>
                                  <v-layout align="center">
                                    <v-radio
                                      @change="updateAndSavePricing"
                                      label="Custom Price"
                                      value="customPrice"
                                    ></v-radio>
                                    <v-text-field
                                      prefix="$"
                                      type="number"
                                      min="1.01"
                                      @input="updateAndSavePricing"
                                      v-model="item.customPrice"
                                      :readonly="
                                        item.priceSelection ===
                                          'customMarkup' ||
                                        !canUpdateCustomerPricing
                                      "
                                      :disabled="
                                        item.priceSelection ===
                                          'customMarkup' ||
                                        !canUpdateCustomerPricing
                                      "
                                      class="ml-6 mb-4"
                                    ></v-text-field>
                                  </v-layout>
                                </v-radio-group>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-icon @click="removeMarkup(index)">
                              <v-icon color="error">delete</v-icon>
                            </v-list-item-icon>
                          </v-list-item>
                          <v-divider
                            v-if="
                              index + 1 <
                              customer.individualProductPrices.length
                            "
                          />
                        </div>
                        <v-list-item
                          v-if="!customer.individualProductPrices.length"
                        >
                          <v-list-item-content>
                            <v-list-item-title>
                              No individual product prices yet.
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-flex>
                  <v-flex xs12 md8 mt-6>
                    <products-table
                      :headers="productTableHeaders"
                      :actions="productTableActions"
                    />
                  </v-flex>
                </v-layout>
              </v-tab-item>
            </v-tabs>
          </v-tab-item>
        </v-tabs>
        <address-modal
          v-if="addressModal"
          :title="addressModalTitle"
          :addressModal.sync="addressModal"
          :address="activeAddress"
          :distributors="distributors"
          @save="saveAddress"
        />
        <user-modal
          v-if="userModal"
          :title="userModalTitle"
          :userModal.sync="userModal"
          :user="activeUser"
          :permissions="permissions"
          action="edit"
          @save="saveUser"
        />
        <add-markup-modal
          v-if="addMarkupModal"
          title="Add Individual Product Pricing"
          :addMarkupModal.sync="addMarkupModal"
          :product="productMarkup"
          @setProductPrice="setProductPrice"
        />
      </div>
    </v-layout>
  </div>
  <div v-else>
    <v-layout align-center justify-center>
      <v-progress-circular
        indeterminate
        color="primary"
        size="100"
      ></v-progress-circular>
    </v-layout>
  </div>
</template>

<script>
import request from "@/modules/request";
import Table from "@/components/Table";
import AddressModal from "@/components/shared/modals/AddressModal";
import UserModal from "@/components/shared/modals/UserModal";
import AddMarkupModal from "@/components/shared/modals/AddMarkupModal";
import { hasPermission } from "@/modules/rbac/permission-manifest";
import moment from "moment";
import ProductsTable from "@/components/ProductsTable";

export default {
  name: "Customer",
  async mounted() {
    const {
      getCompany,
      getDistributors,
      getCategories,
      getPermissions,
      getAssociates,
      getOrders,
    } = this;
    const [
      customer,
      // products,
      distributors,
      categories,
      permissions,
      associates,
      orders,
    ] = await Promise.all([
      getCompany(),
      getDistributors(),
      getCategories(),
      getPermissions(),
      getAssociates(),
      getOrders(),
    ]);

    Object.assign(this, {
      customer: customer,
      distributors,
      categories,
      permissions,
      associates,
      orders,
      loading: false,
    });

    this.initializeMarkups();
    this.setAddressActions();
    this.setUserActions();
    this.loading = false;

    if (this.canUpdateCustomerPricing) {
      this.productTableActions.push({
        color: "primary",
        click: this.setMarkupModal,
        text: "",
        icon: "add",
      });
    }
  },
  inject: ["snackBar", "reactiveInjected"],
  components: {
    Table,
    AddressModal,
    UserModal,
    AddMarkupModal,
    ProductsTable,
  },
  data() {
    return {
      loading: true,
      showProducts: false,
      addressAction: null, // editing or adding
      addressType: null, // billingAddresses or shippingAddresses
      userAction: null, // editing or adding
      customer: null,
      activeAddress: null,
      addressModal: false,
      addingAddress: null,
      productMarkup: null,
      activeUser: null,
      userModal: false,
      addMarkupModal: false,
      addressActions: [],
      userActions: [],
      addressHeaders: [
        {
          text: "Name",
          value: "name",
        },
        {
          text: "Address",
          value: "formatted_address",
        },
        {
          text: "",
          value: "action",
        },
      ],
      userHeaders: [
        {
          text: "First",
          value: "firstName",
        },
        {
          text: "Last",
          value: "lastName",
        },
        {
          text: "Email",
          value: "email",
        },
        {
          text: "Phone",
          value: "phoneNumber",
        },
        {
          text: "Title",
          value: "title",
        },
        {
          text: "User can use app",
          value: "approved",
        },
        {
          text: "Setup Complete",
          value: "isSetup",
        },
        {
          text: "",
          value: "action",
        },
      ],
      markups: [],
      associates: [],
      productTableHeaders: [
        {
          text: "Product ID",
          value: "productCode",
        },
        {
          text: "Manufacturer",
          value: "manufacturer",
        },
        {
          text: "Category",
          value: "category.name",
        },
        {
          text: "Description",
          value: "description[0].value",
        },
        {
          text: "Cost",
          value: "invoicePrice",
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      productTableActions: [],
      orders: [],
      ordersTableHeaders: [
        {
          text: "Order ID",
          value: "orderId",
        },
        {
          text: "PO #",
          value: "poNum",
        },
        {
          text: "Customer",
          value: "company.name",
        },
        {
          text: "Order Date",
          value: "orderDate",
        },
        {
          text: "Status",
          value: "orderStatus",
        },
        {
          text: "Order Total",
          value: "formattedOrderTotal",
        },
        {
          text: "",
          value: "action",
        },
      ],
      ordersTableActions: [
        {
          color: "primary",
          click: this.viewOrder,
          text: "View",
        },
      ],
      categories: [],
      distributorSettings: [],
      distributors: [],
      permissions: [],
      canInviteUser: hasPermission(
        "canInviteUser",
        null,
        this.reactiveInjected.activeUser
      ),
      canCreateOrder: hasPermission(
        "canCreateOrder",
        null,
        this.reactiveInjected.activeUser
      ),
      canUpdateCustomer: hasPermission(
        "canUpdateCustomer",
        null,
        this.reactiveInjected.activeUser
      ),
      canUpdateUser: hasPermission(
        "canUpdateUser",
        null,
        this.reactiveInjected.activeUser
      ),
      canReadCustomerPricing: hasPermission(
        "canReadCustomerPricing",
        null,
        this.reactiveInjected.activeUser
      ),
      canUpdateCustomerPricing: hasPermission(
        "canUpdateCustomerPricing",
        null,
        this.reactiveInjected.activeUser
      ),
    };
  },
  computed: {
    userModalTitle() {
      return this.userAction === "editing" ? "Update User" : "Add User";
    },
    addressModalTitle() {
      return this.addressAction === "editing"
        ? "Update Address"
        : "Add Address";
    },
  },
  methods: {
    gotoNewOrder() {
      this.$router.push({
        name: "AssociateCreateOrder",
      });
    },
    async onTabChange(tabIndex) {},
    async getOrders() {
      const results = await request.callInternal({
        method: "get",
        route: `/api/orders/?company=${this.$route.params.id}`,
      });
      return this.formatOrders(results.data);
    },
    formatOrders(orders) {
      const formattedOrders = orders.map((order) => {
        order.orderDate = moment(order.createdAt).format("MM-DD-YYYY");
        return order;
      });
      return orders;
    },
    viewOrder(order) {
      this.$router.push({
        name: "AssociateOrder",
        params: {
          id: order._id,
        },
      });
    },
    associateName(user) {
      return `${user.lastName}, ${user.firstName}`;
    },
    getAssociates() {
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/associates",
        })
        .then((results) => {
          return results.data;
        });
    },
    setAddressActions() {
      if (this.canUpdateCustomer) {
        this.addressActions.push({
          color: "primary",
          click: this.setEditingAddress,
          text: "Edit",
        });
      }
    },
    setUserActions() {
      if (this.canUpdateUser) {
        this.userActions.push({
          color: "primary",
          click: this.setEditingUser,
          text: "Edit",
        });
      }
    },
    initializeDistributorSettings() {
      const { categories } = this;
      return categories.map((category) => {
        return {
          category: category._id,
          name: category.name,
          distributor: null,
        };
      });
    },
    async getPermissions() {
      return request
        .callInternal({
          method: "get",
          route: `/api/admin/permissions`,
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
          return;
        });
    },
    async getDistributors() {
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/distributorlocations",
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
          return;
        });
    },
    async getCategories() {
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/fullcategories",
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error("Error retrieving categories");
        });
    },
    removeMarkup(idx) {
      this.customer.individualProductPrices.splice(idx, 1);
      this.updateAndSavePricing();
    },
    setProductPrice(ctx) {
      const {
        _id,
        markup,
        customPrice,
        priceSelection,
        description,
        manufacturer,
        productCode,
      } = ctx;
      const added = this.customer.individualProductPrices.find(
        (item) => item._id === _id
      );
      if (!added) {
        this.customer.individualProductPrices.push({
          _id,
          priceSelection,
          customPrice,
          markup,
          description,
          manufacturer,
          productCode,
        });
        this.saveCompany();
      } else {
        this.snackBar("Individual product already added", "error");
      }
      this.addMarkupModal = false;
    },
    setMarkupModal(product) {
      this.productMarkup = product;
      this.addMarkupModal = true;
    },
    updateAndSavePricing() {
      const { markups } = this;
      const categoryMarkups = [];
      let subcategoryMarkups = [];
      markups.forEach((markup) => {
        const { subcategories = [] } = markup;
        subcategoryMarkups = [...subcategoryMarkups, ...subcategories];
        categoryMarkups.push(markup);
        this.customer.categoryMarkups = categoryMarkups;
        this.customer.subcategoryMarkups = subcategoryMarkups;
      });
      this.saveCompany();
    },
    setAddUser() {
      this.activeUser = {
        firstName: null,
        lastName: null,
        company: this.customer._id,
        email: null,
        phoneNumber: null,
        title: null,
        notes: null,
        permissions: [],
      };
      this.userModal = true;
      this.userAction = "adding";
    },
    setAddAddress(type) {
      this.addressType = type;
      this.addressAction = "adding";
      this.addressModal = true;
      this.activeAddress = {
        formatted_address: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        meta: {},
      };
      if (type === "shippingAddresses") {
        this.activeAddress.meta.distributorSettings = this.initializeDistributorSettings();
        this.activeAddress.meta.emailSettings = [];
      }
    },
    setEditingAddress(address) {
      const { distributors } = this;
      this.addressModal = true;
      this.activeAddress = address;
      this.addressAction = "editing";

      const billingAddressIndex = this.customer.billingAddresses.findIndex(
        (addr) => addr._id === address._id
      );
      const shippingAddressIndex = this.customer.shippingAddresses.findIndex(
        (addr) => addr._id === address._id
      );
      if (billingAddressIndex > -1) {
        this.addressType = "billingAddresses";
      } else {
        this.customer.shippingAddresses[shippingAddressIndex] = address;
        this.addressType = "shippingAddresses";
      }
    },
    setEditingUser(user) {
      this.userModal = true;
      this.activeUser = user;
      this.userAction = "editing";
    },
    async getCompany() {
      const id = this.$route.params.id;
      const results = await request
        .callInternal({
          method: "get",
          route: `/api/company/${id}`,
        })
        .catch((error) => {
          console.error(error);
          return;
        });

      return results.data;
    },
    initializeMarkups() {
      const { categoryMarkups, subcategoryMarkups } = this.customer;
      subcategoryMarkups.forEach((markup) => {
        const { category, value } = markup;
        const categoryIndex = categoryMarkups.findIndex(
          (elem) => elem.category._id === category
        );
        if (categoryMarkups[categoryIndex].subcategories) {
          categoryMarkups[categoryIndex].subcategories.push(markup);
        } else {
          categoryMarkups[categoryIndex].subcategories = [markup];
        }
      });
      this.markups = categoryMarkups;
    },
    async saveCompany() {
      const id = this.$route.params.id;
      await request
        .callInternal({
          method: "patch",
          route: `/api/admin/company/${id}`,
          data: this.customer,
        })
        .then(() => {
          this.snackBar("Company saved!", "success");
        })
        .catch((error) => {
          this.snackBar("Sorry, something went wrong", "error");
          console.error(error);
        });
    },
    async updateApproval() {
      await request
        .callInternal({
          method: "post",
          route: "/api/admin/company/approval",
          data: { id: this.customer._id },
        })
        .catch((error) => {
          console.error(error);
        });
    },
    saveAddress(address) {
      const { addressAction, addressType } = this;
      this.addressModal = false;

      if (addressAction === "adding") {
        this.customer[addressType].push(address);
      } else if (addressAction === "editing") {
        const addressIndex = this.customer[addressType].find(
          (addr) => addr._id === address._id
        );
        this.customer[addressType][addressIndex] = address;
      }

      this.saveCompany();
    },
    async saveUser(user) {
      const { userAction } = this;
      if (userAction === "editing") {
        await request
          .callInternal({
            method: "patch",
            route: `/api/admin/updateuser/${user._id}`,
            data: user,
          })
          .then(() => {
            this.snackBar("User saved!", "success");
          })
          .catch((error) => {
            this.snackBar("Sorry, something went wrong", "error");
            console.error(error);
          });
      } else if (userAction === "adding") {
        user.company = this.customer._id;
        const { data: newUsers } = await request
          .callInternal({
            method: "post",
            route: `/api/admin/inviteuser`,
            data: { user },
          })
          .catch((error) => {
            console.error(error);
          });
        newUsers.forEach((user) => {
          this.customer.users.push(user);
        });
      }
      this.userModal = false;
    },
  },
};
</script>
